import React, { forwardRef, useImperativeHandle, Ref } from "react";
import { Row, RowData } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as ShadcnTable from "@/v2/shared/shadcn-ui/table";
import { TableCell } from "./TableCell";

export interface TableBodyRef {
  scrollToIndex: (index: number) => void;
}

interface TableBodyProps<RowDataType extends RowData> {
  rows: Row<RowDataType>[];
  getScrollElement: () => HTMLDivElement | null;
  onRowClicked?: (row: RowDataType) => void;
}

function TableBodyInner<RowDataType extends RowData>(
  { rows, getScrollElement, onRowClicked }: TableBodyProps<RowDataType>,
  ref: Ref<TableBodyRef>
) {
  // Virtualizer setup
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 48,
    getScrollElement,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  // Expose scrollToIndex via the ref
  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index: number) => {
        // scroll so that the item at `index` is visible
        rowVirtualizer.scrollToIndex(index);
      },
    }),
    [rowVirtualizer]
  );

  return (
    <ShadcnTable.TableBody
      style={{
        display: "grid",
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: "relative",
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index] as Row<RowDataType>;
        const handleRowClick =
          onRowClicked != null ? () => onRowClicked(row.original) : undefined;

        return (
          <ShadcnTable.TableRow
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            key={row.id}
            style={{
              display: "flex",
              position: "absolute",
              transform: `translateY(${virtualRow.start}px)`,
              width: "100%",
              overflow: "hidden",
            }}
            onClick={handleRowClick}
          >
            {row.getVisibleCells().map((cell) => {
              return <TableCell key={cell.id} {...cell} />;
            })}
          </ShadcnTable.TableRow>
        );
      })}
    </ShadcnTable.TableBody>
  );
}

// Cast to restore generic signature after forwardRef
export const TableBody = forwardRef(TableBodyInner) as <
  RowDataType extends RowData
>(
  props: TableBodyProps<RowDataType> & { ref?: React.Ref<TableBodyRef> }
) => JSX.Element;
